/* @jsx jsx */
import { css, jsx } from '@emotion/react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { ColorName, Colors, TypographyName } from '../../../theme';
import { Text } from '../../display/Text';
import { TooltipWrapper } from '../../display/TooltipWrapper';

import NestedThirdRow from './NestedThirdRow';
import PageNavigationBadge from './PageNavigationBadge';
import PageNavigationRow from './PageNavigationRow';
import type { ContentProps, OnSelectType, PageNavigationItem } from './types';
import checkIsTruncated from './utils/checkIsTruncated';

const Styles = {
  list: css`
    list-style: none;
    margin: 0;
    padding: 0;
  `,
  textEllipsis: (textColor: ColorName) => css`
    color: ${Colors[textColor]};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  `,
  // The width value for textWrap is 90% of the row width, less the width
  // of the space available for the Badge count (32px) and less the 13px
  // difference in text indentation between the first and second nested
  // rows.
  textWrap: css`
    align-items: center;
    display: flex;
    flex-direction: row;
    width: calc(90% - 45px);
  `,
  rowContainer: css`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    padding: var(--zds-space-8) var(--zds-space-24) var(--zds-space-8)
      var(--zds-space-40);
    position: relative;
  `,
};

export default function NestedSecondRow({
  item,
  onSelect,
}: {
  item: PageNavigationItem;
  onSelect?: OnSelectType;
}) {
  const [isTruncated, setIsTruncated] = useState(false);
  const textRef = useRef<HTMLElement>(null);

  useEffect(() => {
    setIsTruncated(checkIsTruncated(textRef));
  }, []);

  const InnerContent = ({ hover, selected }: ContentProps) => {
    const textColor: ColorName =
      hover || selected ? 'UiPrimaryStrongest' : 'GrayWarm10';
    const textType: TypographyName = selected ? 'Body3' : 'Body2';

    return (
      <div css={Styles.rowContainer} data-zds>
        <span css={Styles.textWrap} data-zds>
          <span css={Styles.textEllipsis(textColor)} ref={textRef} data-zds>
            <Text color={textColor} margin="0 0 0 8px" type={textType}>
              {item.title}
            </Text>
          </span>
        </span>
        {item.count && (
          <PageNavigationBadge variant="neutral">
            {item.count.toString()}
          </PageNavigationBadge>
        )}
      </div>
    );
  };

  const Content = ({ hover, selected }: ContentProps) =>
    isTruncated ? (
      <TooltipWrapper
        allowMultilineTooltip={true}
        content={item.title}
        isBlock={true}
        position="northeast"
      >
        {() => <InnerContent hover={hover} selected={selected} />}
      </TooltipWrapper>
    ) : (
      <InnerContent hover={hover} selected={selected} />
    );

  const NestedList = useMemo(
    () =>
      item.children
        ? () => (
            <ul css={Styles.list} data-zds>
              {item.children?.map((i) => (
                <NestedThirdRow item={i} key={i.id} onSelect={onSelect} />
              ))}
            </ul>
          )
        : undefined,
    [item, onSelect]
  );

  return (
    <PageNavigationRow
      content={Content}
      item={item}
      nestedList={NestedList}
      onSelect={onSelect}
    />
  );
}
